import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from 'src/environments/environment';
import { CHATBOT_API_PATHS, CHATBOT_TEXT } from '@core/constants/chatbot.constants';
import {
  ChatApiRequest,
  ChatApiResponse,
  ChatFeedbackRequest,
  ChatFeedbackResponse,
  ChatFeedbackType,
  ChatHistoryGroupedSection,
  ChatbotMessage,
} from '@core/interfaces/chatbot.interface';
import { UserService } from '../user/user.service';
import { detectMarket } from '@app/core/utils/market.util';

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly userService = inject(UserService);

  private readonly chatUrl = this.buildApiUrl(CHATBOT_API_PATHS.CHAT);
  private readonly historyUrl = this.buildApiUrl(CHATBOT_API_PATHS.HISTORY);
  private readonly feedbackUrl = this.buildApiUrl(CHATBOT_API_PATHS.FEEDBACK);

  /**
   * Session ID generated once per application session; persists for the lifetime
   * of the service so the backend can correlate messages within the same session.
   */
  private readonly sessionId = this.generateSessionId();

  /** Time formatter reused for each bot message timestamp. */
  private readonly timeLabelOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  /**
   * Sends a user message to the chatbot API.
   * Resolves the current access token and user ID automatically before the call.
   */
  sendMessage(message: string): Observable<ChatbotMessage> {
    const userId = this.resolveUserId();

    return from(
      this.auth.getAccessTokenSilently({
        authorizationParams: { audience: environment.auth0.audience },
      }),
    ).pipe(
      catchError(() => of('')),
      switchMap((token) => {
        const body: ChatApiRequest = {
          user_id: userId,
          session_id: this.sessionId,
          message,
          region: this.getRegion(),
          authorization_token: token ?? '',
        };
        return this.http.post<ChatApiResponse>(this.chatUrl, body);
      }),
      map((response) => this.mapResponseToMessage(response)),
    );
  }

  /** Fetches all history rows for the authenticated user. */
  getGroupedHistoryByUser(): Observable<ChatHistoryGroupedSection[]> {
    const userId = this.resolveUserId();
    if (!userId) {
      return of([]);
    }

    return this.http.get<ChatHistoryGroupedSection[]>(this.buildGroupedHistoryUrl(userId));
  }

  /** Submits optional thumbs up/down feedback for a bot response. */
  submitFeedback(
    question: string,
    answer: string,
    feedbackType: ChatFeedbackType,
  ): Observable<ChatFeedbackResponse> {
    const userId = this.resolveUserId();
    const body: ChatFeedbackRequest = {
      user_id: userId,
      session_id: this.sessionId,
      question,
      answer,
      feedback_type: feedbackType,
    };

    return this.http.post<ChatFeedbackResponse>(this.feedbackUrl, body);
  }

  /** Triggers a chatbot FAQ search-index rebuild after an FAQ CRUD operation. */
  buildFaqIndex(): Observable<void> {
    return this.http.post<void>(this.buildApiUrl(CHATBOT_API_PATHS.FAQ_INDEX_BUILD), {});
  }

  /** Triggers a chatbot Contact Us search-index rebuild after a Contact CRUD operation. */
  buildContactIndex(): Observable<void> {
    return this.http.post<void>(this.buildApiUrl(CHATBOT_API_PATHS.CONTACT_INDEX_BUILD), {});
  }

  /** Builds a chatbot endpoint URL from environment base URL and endpoint path. */
  private buildApiUrl(endpoint: string): string {
    const baseUrl = environment.chatbotApiUrl ?? '';
    return `${baseUrl}${endpoint}`;
  }

  /** Builds the grouped-history endpoint for one user id. */
  private buildGroupedHistoryUrl(userId: string): string {
    return `${this.historyUrl}/${encodeURIComponent(userId)}${CHATBOT_API_PATHS.HISTORY_GROUPED_SUFFIX}`;
  }

  /** Extracts a stable user identifier from the loaded user profile. */
  private resolveUserId(): string {
    const data = this.userService.userApiData();
    return String(data?.UserKey ?? data?.userId ?? data?.Id ?? '');
  }

  /** Maps the raw API response to a local {@link ChatbotMessage} for rendering. */
  private mapResponseToMessage(response: ChatApiResponse): ChatbotMessage {
    // Use client-local current time so live chat bubbles reflect when the reply is shown in UI.
    const timestamp = new Date().toLocaleTimeString([], this.timeLabelOptions);

    return {
      id: `bot-${response.created_at ?? Date.now()}`,
      text: response.answer || CHATBOT_TEXT.FALLBACK_BOT_REPLY,
      sender: 'bot',
      timestamp,
    };
  }

  /** Generates a cryptographically-random session ID, falling back to a timestamp-based id. */
  private generateSessionId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `session-${Date.now()}`;
  }

  /**
   * Resolves chatbot region.
   * Priority: loaded user country -> market detection (query/hostname/default market).
   */
  private getRegion(): 'au' | 'nz' {
    const profileCountry = (this.userService.userProfile().country ?? '').trim().toLowerCase();
    const apiCountry = (this.userService.userApiData()?.Country ?? '').trim().toLowerCase();
    const country = profileCountry || apiCountry;

    if (country === 'nz' || country.includes('new zealand')) {
      return 'nz';
    }

    if (country === 'au' || country.includes('australia')) {
      return 'au';
    }

    return detectMarket();
  }
}
